import { useCallback } from 'react';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import { MagpieConnection } from 'magpie-lib/connection';
import { VideoManagementMessages, videoManagementTopicDef } from '../topics/videoManagement';
import { waitForConnectedMagpie } from '../../utils/magpie';
export const usePublishClip = () => {
  return useCallback(renderId => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.clipPublishedMessage.create({
      renderId
    });
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
};

/**
 * @deprecated Use usePublishClip instead
 * Kept for backwards compatibility with content-media-composition
 */
export const usePublishVariant = usePublishClip;
export const useInsertVideo = () => {
  return useCallback(video => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.insertVideoMessage.create({
      video
    });
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
};
export const useBulkInsertVideos = () => {
  return useCallback(videos => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.bulkInsertVideosMessage.create({
      videos
    });
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
};
export const useVideoDeleted = () => {
  return useCallback((sourceFileId, videoTitle) => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.videoDeletedMessage.create({
      sourceFileId,
      videoTitle
    });
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
};
export const useNotifyUser = () => {
  const sendNotifyUserMessage = useCallback(plainTextAlert => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.notifyUserMessage.create(plainTextAlert);
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
  return sendNotifyUserMessage;
};
export const useCloseVideoPanel = () => {
  return useCallback(() => {
    const connection = MagpieConnection.get();
    const message = VideoManagementMessages.videoPanelClosedMessage.create({});
    connection.useTopic(videoManagementTopicDef).publish(message);
  }, []);
};
export const useRegisterPublishClip = ({
  onPublish
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.clipPublishedMessage.getType(), async message => {
      await onPublish(message.payload.renderId);
    });
    return () => unsubscribe();
  }, [onPublish]);
};

/**
 * @deprecated Use useRegisterPublishClip instead
 * Kept for backwards compatibility with content-media-composition
 */
export const useRegisterPublishVariant = useRegisterPublishClip;
export const useRegisterInsertVideo = ({
  onInsert
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.insertVideoMessage.getType(), async message => {
      const {
        video
      } = message.payload;
      await onInsert(video);
    });
    return () => unsubscribe();
  }, [onInsert]);
};
export const useRegisterBulkInsertVideos = ({
  onBulkInsert
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.bulkInsertVideosMessage.getType(), async message => {
      const {
        videos
      } = message.payload;
      await onBulkInsert(videos);
    });
    return () => unsubscribe();
  }, [onBulkInsert]);
};
export const useRegisterVideoDeleted = ({
  onVideoDeleted
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.videoDeletedMessage.getType(), async message => {
      const {
        sourceFileId,
        videoTitle
      } = message.payload;
      await onVideoDeleted(sourceFileId, videoTitle);
    });
    return () => unsubscribe();
  }, [onVideoDeleted]);
};
export const useRegisterNotifyUser = ({
  onNotifyUser
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.notifyUserMessage.getType(), async message => {
      await onNotifyUser(message.payload);
    });
    return () => unsubscribe();
  }, [onNotifyUser]);
};
export const useRegisterCloseVideoPanel = ({
  onClose
}) => {
  useAsyncEffect(async () => {
    await waitForConnectedMagpie();
    const connection = MagpieConnection.get();
    const unsubscribe = connection.useTopic(videoManagementTopicDef).on(VideoManagementMessages.videoPanelClosedMessage.getType(), () => onClose());
    return () => unsubscribe();
  }, [onClose]);
};